import { motion } from 'framer-motion';
import { Home, Shield, Users, Clock, Package, Smile, CheckCircle } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const benefits = [
  {
    icon: Shield,
    title: 'Mais Segurança',
    description: 'Elimine o risco de encomendas perdidas ou roubadas na portaria.',
  },
  {
    icon: Users,
    title: 'Menos Trabalho',
    description: 'Porteiros focados em segurança, não em gestão de entregas.',
  },
  {
    icon: Clock,
    title: 'Disponível 24/7',
    description: 'Moradores retiram suas encomendas a qualquer hora.',
  },
  {
    icon: Package,
    title: 'Organização',
    description: 'Fim das encomendas acumuladas na portaria.',
  },
  {
    icon: Smile,
    title: 'Satisfação',
    description: 'Moradores mais felizes com a praticidade do serviço.',
  },
  {
    icon: CheckCircle,
    title: 'Rastreamento',
    description: 'Controle total de todas as entregas e retiradas.',
  },
];

const personas = [
  {
    title: 'Para Síndicos',
    items: [
      'Valorização do condomínio',
      'Redução de reclamações sobre entregas',
      'Relatórios de uso detalhados',
      'Suporte dedicado',
    ],
  },
  {
    title: 'Para Porteiros',
    items: [
      'Menos interrupções no trabalho',
      'Sem responsabilidade por encomendas',
      'Interface simples para uso',
      'Treinamento incluso',
    ],
  },
  {
    title: 'Para Moradores',
    items: [
      'Retirada no horário que preferir',
      'Notificação instantânea',
      'Segurança garantida',
      'App fácil de usar',
    ],
  },
];

export const CondominiosPage = () => {
  return (
    <main className="pt-20" data-testid="condominios-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-lokker-blue relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-sm mb-6">
                <Home className="w-5 h-5 text-lokker-lime" />
                <span className="text-white font-medium">Para Condomínios</span>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
                Modernize a gestão de entregas do seu condomínio
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Lockers inteligentes que reduzem o trabalho da portaria, aumentam a segurança 
                e oferecem praticidade aos moradores.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#solicitar-proposta"
                  className="btn-secondary px-8 py-4 rounded-sm inline-flex items-center gap-2"
                  data-testid="condo-hero-cta"
                >
                  Solicitar Proposta
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="absolute -inset-4 border-2 border-lokker-lime rounded-sm transform rotate-3" />
              <img
                src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&h=400&fit=crop"
                alt="Condomínio moderno"
                className="relative rounded-sm w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
              Vantagens
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Por que ter Use Lokker no seu condomínio?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`condo-benefit-${index + 1}`}
                className="bg-slate-50 border border-slate-200 p-6 rounded-sm card-hover"
              >
                <div className="w-12 h-12 bg-lokker-blue rounded-sm flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Benefícios para todos
            </h2>
            <p className="text-slate-600">
              Uma solução que agrada síndicos, porteiros e moradores.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {personas.map((persona, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`condo-persona-${index + 1}`}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden"
              >
                <div className="bg-lokker-blue p-4">
                  <h3 className="font-heading font-bold text-xl text-white">
                    {persona.title}
                  </h3>
                </div>
                <ul className="p-6 space-y-3">
                  {persona.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-lokker-lime rounded-sm flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3 h-3 text-black" />
                      </div>
                      <span className="text-slate-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Model */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
                Modelo de Assinatura
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
                Investimento que se paga
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Oferecemos um modelo de assinatura mensal que inclui:
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-lokker-blue rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Instalação completa</span>
                    <p className="text-slate-600 text-sm">Equipamentos e configuração sem custo adicional.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-lokker-blue rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Manutenção preventiva</span>
                    <p className="text-slate-600 text-sm">Visitas periódicas e suporte técnico inclusos.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-lokker-blue rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Treinamento da equipe</span>
                    <p className="text-slate-600 text-sm">Capacitação para porteiros e administração.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-lokker-blue rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Painel de gestão</span>
                    <p className="text-slate-600 text-sm">Relatórios e controle total das entregas.</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-slate-900 p-8 rounded-sm"
            >
              <div className="text-center">
                <p className="text-lokker-lime font-medium mb-2">A partir de</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-white/60">R$</span>
                  <span className="font-heading font-bold text-6xl text-white">999</span>
                  <span className="text-white/60">/mês</span>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  *Valor varia de acordo com o número de compartimentos
                </p>
                <div className="border-t border-slate-700 pt-6">
                  <p className="text-white mb-4">O que está incluso:</p>
                  <ul className="space-y-2 text-left">
                    <li className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-lokker-lime" />
                      Locker com 8 a 24 compartimentos
                    </li>
                    <li className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-lokker-lime" />
                      App para moradores
                    </li>
                    <li className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-lokker-lime" />
                      Painel administrativo
                    </li>
                    <li className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-lokker-lime" />
                      Suporte 24/7
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="solicitar-proposta" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
                Entre em Contato
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
                Solicite uma proposta para seu condomínio
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Preencha o formulário ao lado e nossa equipe comercial entrará em contato 
                em até 24 horas úteis para apresentar a melhor solução para seu condomínio.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-lokker-blue rounded-sm flex items-center justify-center">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <p className="text-slate-700">Preencha o formulário</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-lokker-blue rounded-sm flex items-center justify-center">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <p className="text-slate-700">Receba nossa proposta personalizada</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-lokker-blue rounded-sm flex items-center justify-center">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <p className="text-slate-700">Agende a instalação</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ContactForm
                type="condominio"
                title="Solicitar Proposta"
                description="Preencha seus dados e entraremos em contato."
              />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CondominiosPage;
